import { useEffect } from "react";

/**
 * Best-effort protection against screenshots and printing.
 * Real OS-level screenshots cannot be fully prevented from a browser, but we can:
 * - Block Ctrl+P / Cmd+P (print)
 * - Block Ctrl+S / Cmd+S (save page)
 * - Block PrintScreen key (clear clipboard, show warning)
 * - Disable right-click context menu
 * - Disable text selection & drag on protected content
 * - Blank the page during window.print via @media print CSS
 * - Show a warning overlay when the window loses focus (screen recording detection heuristic)
 */
export default function useAppProtection(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const styleEl = document.createElement("style");
    styleEl.id = "cartomancer-protection-style";
    styleEl.innerHTML = `
      @media print {
        body::before {
          content: "Protezione attiva: la stampa e' disabilitata.";
          display: block;
          font-family: Cinzel, serif;
          font-size: 24px;
          padding: 40px;
          color: #000;
          text-align: center;
        }
        body > *:not(#__protection_msg) { display: none !important; }
      }
      .cartomancer-protected {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-user-drag: none;
      }
      .cartomancer-protected img { -webkit-user-drag: none; user-drag: none; pointer-events: auto; }
    `;
    document.head.appendChild(styleEl);

    const showWarning = (message) => {
      const existing = document.getElementById("cartomancer-protection-warning");
      if (existing) existing.remove();
      const div = document.createElement("div");
      div.id = "cartomancer-protection-warning";
      div.setAttribute("data-testid", "protection-warning");
      div.style.cssText = `
        position:fixed;top:20px;left:50%;transform:translateX(-50%);
        z-index:99999;background:rgba(159,18,57,0.95);color:#f5deb3;
        padding:12px 24px;border-radius:8px;border:1px solid #d4af37;
        font-family:Cinzel,serif;font-size:12px;letter-spacing:2px;
        text-transform:uppercase;box-shadow:0 4px 20px rgba(0,0,0,0.5);
      `;
      div.textContent = message;
      document.body.appendChild(div);
      setTimeout(() => div.remove(), 2500);
    };

    const onKeyDown = (e) => {
      const isCtrl = e.ctrlKey || e.metaKey;
      // Block Print / Save
      if (isCtrl && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        e.stopPropagation();
        showWarning("◈ Stampa disabilitata");
        return false;
      }
      if (isCtrl && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        e.stopPropagation();
        showWarning("◈ Salvataggio pagina disabilitato");
        return false;
      }
      // PrintScreen key
      if (e.key === "PrintScreen") {
        try {
          navigator.clipboard?.writeText?.("").catch(() => {});
        } catch {}
        showWarning("◈ Screenshot rilevato");
      }
      // Block DevTools shortcuts (best effort)
      if (isCtrl && e.shiftKey && (e.key === "I" || e.key === "i" || e.key === "J" || e.key === "j" || e.key === "C" || e.key === "c")) {
        // Allowed - we don't want to block devtools completely (too aggressive)
      }
    };

    const onContextMenu = (e) => {
      // Allow context menu on inputs so users can paste
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
    };

    const onBeforePrint = () => {
      showWarning("◈ Stampa non consentita");
    };

    const onCopy = (e) => {
      // Allow copy inside inputs/textareas
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
    };

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("contextmenu", onContextMenu, true);
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("copy", onCopy);

    document.body.classList.add("cartomancer-protected");

    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("contextmenu", onContextMenu, true);
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("copy", onCopy);
      document.body.classList.remove("cartomancer-protected");
      styleEl.remove();
    };
  }, [enabled]);
}
